import { IImageFocus } from "@eventr/common";
import FallbackHeaderImageSource from "assets/illustrations/defaultSplash.png";
import { SlideImage } from "./slide/SlideImage";

interface CarouselSlideBackgroundProps {
  children?: React.ReactNode;
  /** The itinerary's cover image, if it has one. */
  src?: string;
  /**
   * Whether the cover image should be painted yet. The carousel defers off-screen slides,
   * and a deferred slide must stay empty rather than fall back to the splash: it *has* a
   * picture, it just hasn't been asked for one yet. Defaults to true.
   */
  load?: boolean;
  focus?: IImageFocus;
  /** Told the picture once it is painted, with the box it covers. */
  onPainted?: (url: string, frame: { width: number; height: number }) => void;
}

export function CarouselSlideBackground({ children, src, load = true, focus, onPainted }: CarouselSlideBackgroundProps) {
  // An empty string is "no cover image" in the itinerary payload, so `||` not `??`.
  const coverImage = src || undefined;

  // The fallback splash belongs only to an itinerary with no cover image at all. Showing it
  // while a real cover image is still deferred or in flight made the default splash flash in
  // on every slide before the itinerary's own picture replaced it.
  const imageSource = coverImage ? (load ? coverImage : "") : FallbackHeaderImageSource;

  return (
    <SlideImage
      addLoading
      src={imageSource}
      // A focus describes the real cover image. The fallback splash is a different picture
      // and must never be framed by it.
      focus={coverImage ? focus : undefined}
      onPainted={onPainted}
      style={{ zIndex: -1, height: "100%" }}
    >
      {children}
    </SlideImage>
  );
}
